const clone = (value) =>
  JSON.parse(JSON.stringify(value));

const zone = (
  id,
  type,
  partId,
  position,
  size,
) => ({
  id,
  type,
  part_id: partId,
  position,
  size,
  locked: type === 'qr',
});

const block = ({
  id,
  name,
  type = 'box',
  size,
  position,
  rotation = [0, 0, 0],
  material = 'acrylic',
  color = '#ffffff',
  opacity = 1,
  radius = 0,
  role = 'structure',
}) => ({
  id,
  name,
  type,
  size,
  position,
  rotation,
  material,
  color,
  opacity,
  radius,
  role,
});

export const QR_PRODUCT_PRESETS = {
  premium_acrylic: {
    key: 'premium_acrylic',
    label: 'Premium acrylic',
    description:
      'Layered clear acrylic with a weighted base and floating print panel.',
    definition: {
      version: 2,
      design_system: 'premium_blocks',
      preset_key: 'premium_acrylic',

      parts: [
        block({
          id: 'base-main',
          name: 'Weighted base',
          size: [154, 14, 62],
          position: [0, 7, 18],
          material: 'acrylic',
          color: '#EAF4F1',
          opacity: 0.96,
          radius: 8,
          role: 'base',
        }),

        block({
          id: 'base-inset',
          name: 'Base inset',
          size: [128, 5, 42],
          position: [0, 15, 10],
          material: 'metal',
          color: '#B9C5C1',
          radius: 4,
          role: 'accent',
        }),

        block({
          id: 'main-panel',
          name: 'Floating acrylic panel',
          size: [126, 178, 7],
          position: [0, 105, 4],
          rotation: [-5, 0, 0],
          material: 'acrylic',
          color: '#F7FFFC',
          opacity: 0.78,
          radius: 9,
          role: 'face',
        }),

        block({
          id: 'inner-panel',
          name: 'Printed insert',
          size: [108, 150, 2.5],
          position: [0, 105, 9],
          rotation: [-5, 0, 0],
          material: 'plastic',
          color: '#FFFFFF',
          radius: 7,
          role: 'print_surface',
        }),

        block({
          id: 'top-accent',
          name: 'Top accent',
          size: [54, 4, 3],
          position: [0, 175, 11],
          rotation: [-5, 0, 0],
          material: 'metal',
          color: '#C59B5F',
          radius: 2,
          role: 'accent',
        }),
      ],

      customization_zones: [
        zone(
          'brand-zone',
          'restaurant_name',
          'inner-panel',
          [0, 151, 11],
          [88, 22],
        ),

        zone(
          'qr-zone',
          'qr',
          'inner-panel',
          [0, 104, 11],
          [72, 72],
        ),

        zone(
          'cta-zone',
          'custom_text',
          'inner-panel',
          [0, 55, 11],
          [88, 22],
        ),
      ],
    },
  },

  walnut_acrylic: {
    key: 'walnut_acrylic',
    label: 'Walnut + acrylic',
    description:
      'Warm wooden plinth, clear insert and brass detailing for premium dining.',

    definition: {
      version: 2,
      design_system: 'premium_blocks',
      preset_key: 'walnut_acrylic',

      parts: [
        block({
          id: 'base-main',
          name: 'Walnut plinth',
          size: [158, 22, 66],
          position: [0, 11, 18],
          material: 'wood',
          color: '#6F4A31',
          radius: 7,
          role: 'base',
        }),

        block({
          id: 'base-slot',
          name: 'Brass slot',
          size: [124, 5, 35],
          position: [0, 23, 8],
          material: 'metal',
          color: '#C7A56A',
          radius: 3,
          role: 'accent',
        }),

        block({
          id: 'main-panel',
          name: 'Clear acrylic insert',
          size: [122, 174, 6],
          position: [0, 110, 2],
          rotation: [-4, 0, 0],
          material: 'acrylic',
          color: '#F3FFFB',
          opacity: 0.72,
          radius: 10,
          role: 'face',
        }),

        block({
          id: 'print-panel',
          name: 'Warm print panel',
          size: [104, 146, 2.2],
          position: [0, 110, 7],
          rotation: [-4, 0, 0],
          material: 'plastic',
          color: '#FFF9F0',
          radius: 8,
          role: 'print_surface',
        }),

        block({
          id: 'brand-rail',
          name: 'Brass brand rail',
          size: [66, 3, 3],
          position: [0, 174, 9],
          rotation: [-4, 0, 0],
          material: 'metal',
          color: '#C7A56A',
          radius: 2,
          role: 'accent',
        }),
      ],

      customization_zones: [
        zone(
          'brand-zone',
          'restaurant_name',
          'print-panel',
          [0, 153, 9],
          [88, 22],
        ),

        zone(
          'qr-zone',
          'qr',
          'print-panel',
          [0, 106, 9],
          [70, 70],
        ),

        zone(
          'cta-zone',
          'custom_text',
          'print-panel',
          [0, 59, 9],
          [88, 24],
        ),
      ],
    },
  },

  brushed_metal: {
    key: 'brushed_metal',
    label: 'Brushed metal',
    description:
      'Architectural metal plate with raised print surface and precision feet.',

    definition: {
      version: 2,
      design_system: 'premium_blocks',
      preset_key: 'brushed_metal',

      parts: [
        block({
          id: 'base-main',
          name: 'Metal pedestal',
          size: [150, 12, 64],
          position: [0, 6, 18],
          material: 'metal',
          color: '#2F3437',
          radius: 8,
          role: 'base',
        }),

        block({
          id: 'face-shell',
          name: 'Brushed metal shell',
          size: [122, 176, 8],
          position: [0, 104, 0],
          rotation: [-3, 0, 0],
          material: 'metal',
          color: '#3B4145',
          radius: 10,
          role: 'face',
        }),

        block({
          id: 'print-panel',
          name: 'Inset print panel',
          size: [100, 146, 3],
          position: [0, 104, 6],
          rotation: [-3, 0, 0],
          material: 'plastic',
          color: '#F8F8F6',
          radius: 8,
          role: 'print_surface',
        }),

        block({
          id: 'accent-rail',
          name: 'Metal accent rail',
          size: [100, 5, 4],
          position: [0, 173, 8],
          rotation: [-3, 0, 0],
          material: 'metal',
          color: '#C9A463',
          radius: 2,
          role: 'accent',
        }),
      ],

      customization_zones: [
        zone(
          'brand-zone',
          'restaurant_name',
          'print-panel',
          [0, 151, 8],
          [84, 22],
        ),

        zone(
          'qr-zone',
          'qr',
          'print-panel',
          [0, 104, 8],
          [70, 70],
        ),

        zone(
          'cta-zone',
          'custom_text',
          'print-panel',
          [0, 58, 8],
          [84, 22],
        ),
      ],
    },
  },

  folded_tent: {
    key: 'folded_tent',
    label: 'Folded table tent',
    description:
      'Contemporary folded display with a slim footprint for cafés and bars.',

    definition: {
      version: 2,
      design_system: 'premium_blocks',
      preset_key: 'folded_tent',

      parts: [
        block({
          id: 'front-panel',
          name: 'Front panel',
          size: [116, 162, 5],
          position: [0, 90, 18],
          rotation: [-14, 0, 0],
          material: 'metal',
          color: '#252A2C',
          radius: 10,
          role: 'face',
        }),

        block({
          id: 'rear-panel',
          name: 'Rear support',
          size: [116, 162, 5],
          position: [0, 90, -18],
          rotation: [14, 0, 0],
          material: 'metal',
          color: '#1C2022',
          radius: 10,
          role: 'support',
        }),

        block({
          id: 'print-panel',
          name: 'Front print panel',
          size: [96, 136, 2.4],
          position: [0, 92, 23],
          rotation: [-14, 0, 0],
          material: 'plastic',
          color: '#FFFFFF',
          radius: 8,
          role: 'print_surface',
        }),

        block({
          id: 'foot',
          name: 'Grip foot',
          size: [126, 7, 44],
          position: [0, 4, 0],
          material: 'rubber',
          color: '#16191A',
          radius: 4,
          role: 'base',
        }),
      ],

      customization_zones: [
        zone(
          'brand-zone',
          'restaurant_name',
          'print-panel',
          [0, 136, 25],
          [80, 20],
        ),

        zone(
          'qr-zone',
          'qr',
          'print-panel',
          [0, 94, 25],
          [66, 66],
        ),

        zone(
          'cta-zone',
          'custom_text',
          'print-panel',
          [0, 51, 25],
          [80, 20],
        ),
      ],
    },
  },

  counter_display: {
    key: 'counter_display',
    label: 'Counter display',
    description:
      'Layered counter sign with deep base, raised panel and a luxury accent cap.',

    definition: {
      version: 2,
      design_system: 'premium_blocks',
      preset_key: 'counter_display',

      parts: [
        block({
          id: 'base-main',
          name: 'Deep counter base',
          size: [170, 18, 74],
          position: [0, 9, 20],
          material: 'wood',
          color: '#34302C',
          radius: 10,
          role: 'base',
        }),

        block({
          id: 'panel-shell',
          name: 'Raised shell',
          size: [132, 166, 10],
          position: [0, 103, 3],
          rotation: [-5, 0, 0],
          material: 'wood',
          color: '#45403A',
          radius: 12,
          role: 'face',
        }),

        block({
          id: 'print-panel',
          name: 'Inset face',
          size: [108, 140, 3],
          position: [0, 103, 10],
          rotation: [-5, 0, 0],
          material: 'plastic',
          color: '#FFFDF8',
          radius: 9,
          role: 'print_surface',
        }),

        block({
          id: 'top-cap',
          name: 'Top cap',
          size: [76, 5, 5],
          position: [0, 177, 12],
          rotation: [-5, 0, 0],
          material: 'metal',
          color: '#C69D62',
          radius: 3,
          role: 'accent',
        }),
      ],

      customization_zones: [
        zone(
          'brand-zone',
          'restaurant_name',
          'print-panel',
          [0, 148, 12],
          [88, 22],
        ),

        zone(
          'qr-zone',
          'qr',
          'print-panel',
          [0, 103, 12],
          [70, 70],
        ),

        zone(
          'cta-zone',
          'custom_text',
          'print-panel',
          [0, 58, 12],
          [88, 22],
        ),
      ],
    },
  },
};

export const DEFAULT_QR_PRODUCT_PRESET =
  'premium_acrylic';

export const getQrProductPreset = (key) => {
  return (
    QR_PRODUCT_PRESETS[key] ||
    QR_PRODUCT_PRESETS[
      DEFAULT_QR_PRODUCT_PRESET
    ]
  );
};

export const getQrProductPresetDefinition = (
  key,
) => {
  return clone(
    getQrProductPreset(key)
      .definition,
  );
};

export const inferQrProductPreset = ({
  productType,
  material,
} = {}) => {
  const haystack =
    `${productType || ''} ${material || ''}`
      .toLowerCase();

  if (
    haystack.includes('wood') ||
    haystack.includes('walnut') ||
    haystack.includes('oak') ||
    haystack.includes('bamboo')
  ) {
    return 'walnut_acrylic';
  }

  if (
    haystack.includes('metal') ||
    haystack.includes('steel') ||
    haystack.includes('aluminium') ||
    haystack.includes('aluminum') ||
    haystack.includes('plate')
  ) {
    return 'brushed_metal';
  }

  if (
    haystack.includes('counter')
  ) {
    return 'counter_display';
  }

  if (
    haystack.includes('tent') ||
    haystack.includes('card')
  ) {
    return 'folded_tent';
  }

  return DEFAULT_QR_PRODUCT_PRESET;
};

export const normalizePremiumDefinition = (
  definition,
  fallbackKey =
    DEFAULT_QR_PRODUCT_PRESET,
) => {
  if (
    definition?.design_system ===
      'premium_blocks' &&
    Array.isArray(
      definition?.parts,
    ) &&
    definition.parts.length > 0
  ) {
    return clone(definition);
  }

  return getQrProductPresetDefinition(
    fallbackKey,
  );
};